import pricing from '@/common/data/pricing.json';
import {
  BillingCycle,
  LicensePlan,
  OrganisationLicense,
  PlanBreakdown,
} from '@/common/types/organisation';
import dayjs from 'dayjs';

export const PLAN_DETAILS =
  import.meta.env.MODE === 'development'
    ? pricing.development
    : pricing.production;

export const MULTI_YEAR_DISCOUNTS = {
  monthly: 0,
  '1year': 0,
  '2year': 0.1,
  '3year': 0.2,
};

export function getPriceId(
  plan: LicensePlan,
  billingCycle: BillingCycle,
  currency: 'AUD' | 'NZD' | 'USD',
): string {
  const planDetails = PLAN_DETAILS[plan];
  const prices = planDetails.priceId as Record<
    'AUD' | 'NZD' | 'USD',
    Record<BillingCycle, string>
  >;
  if (!prices[currency]?.[billingCycle]) {
    throw new Error(
      `Price ID not found for plan ${plan} with billing cycle ${billingCycle} and currency ${currency}`,
    );
  }
  return prices[currency][billingCycle];
}

export function getBillingCycleLabel(billingCycle: BillingCycle) {
  switch (billingCycle) {
    case 'monthly':
      return 'monthly';
    case '1year':
      return 'yearly';
    case '2year':
      return '2-yearly';
    case '3year':
      return '3-yearly';
    default:
      return billingCycle;
  }
}

export function getNextBillDate(
  billingCycle: BillingCycle,
  billingCycleAnchor: number,
): number {
  const anchorDate = dayjs(billingCycleAnchor);
  const now = dayjs();

  if (now < anchorDate) {
    return anchorDate.valueOf();
  }

  const computeNext = (unit: dayjs.UnitType, length = 1) => {
    const diff = now.diff(anchorDate, unit);
    const intervals =
      length > 1 && unit === 'year' ? Math.floor(diff / length) : diff;
    const toAdd =
      length > 1 && unit === 'year' ? (intervals + 1) * length : intervals + 1;
    return anchorDate.add(toAdd, unit as dayjs.ManipulateType).valueOf();
  };

  switch (billingCycle) {
    case 'monthly': {
      return computeNext('month');
    }
    case '1year': {
      return computeNext('year');
    }
    case '2year': {
      return computeNext('year', 2);
    }
    case '3year': {
      return computeNext('year', 3);
    }
    default: {
      return anchorDate.valueOf();
    }
  }
}

export function getTrialDaysRemaining(trialEndDate: number) {
  const now = dayjs();
  const trialEnd = dayjs(trialEndDate);

  // Use float to count partial days (e.g. 36 hours => 1.5 days)
  const diffFloat = trialEnd.diff(now, 'day', true);

  // Round up to nearest day (eg. 1.5 days => 2 days)
  return Math.max(0, Math.ceil(diffFloat));
}

export function getRecommendedPlan(seatCount: number | null): LicensePlan {
  let cheapestPlan: LicensePlan | undefined;
  let minPrice = Number.MAX_SAFE_INTEGER;

  Object.entries(PLAN_DETAILS).forEach(([planKey, planDetails]) => {
    const dontRecommend = ['trial', 'free', 'partner', 'xlarge'];
    if (dontRecommend.includes(planKey)) return;

    const additionalSeats = seatCount ? seatCount - planDetails.seatCount : 0;
    const extraCost =
      additionalSeats > 0
        ? additionalSeats * planDetails.additionalSeatPrice
        : 0;

    const totalPrice = planDetails.price + extraCost;

    if (totalPrice <= minPrice) {
      minPrice = totalPrice;
      cheapestPlan = planKey as LicensePlan;
    }
  });

  if (!cheapestPlan) {
    cheapestPlan = 'trial';
  }

  return cheapestPlan;
}

export function getPlanDescription(plan: LicensePlan, seatCount: number) {
  const planDetails = PLAN_DETAILS[plan];
  if (plan === 'trial') {
    return 'Free for unlimited users and learners';
  }
  if (plan === 'partner') {
    return 'Enjoy unlimited users and learners on us! Thanks for your support ❤️';
  }
  const additionalSeats = seatCount - planDetails.seatCount;
  let description = `${planDetails.seatCount} learner accounts and unlimited users`;
  if (additionalSeats > 0) {
    description += ` + ${additionalSeats} additional learner accounts`;
  }
  return description;
}

export function formatPrice(
  price: number,
  currency: 'USD' | 'AUD' | 'NZD',
  showCurrency = true,
  showDecimal = true,
) {
  if (isNaN(price) || price < 0) return showCurrency ? 'NZ$0.00' : '$0.00';
  return price.toLocaleString(undefined, {
    style: 'currency',
    currency,
    currencyDisplay: showCurrency ? 'symbol' : 'narrowSymbol',
    minimumFractionDigits: showDecimal ? 2 : 0,
    maximumFractionDigits: showDecimal ? 2 : 0,
  });
}

/**
 * Checks if we are still in the first billing cycle since the coupon was applied.
 */
export function getCouponValid(
  couponAppliedAt: number | undefined,
  billingCycle: BillingCycle,
): boolean {
  if (!couponAppliedAt) return false;

  const couponAppliedAtDate = dayjs(couponAppliedAt);
  const now = dayjs();

  // Calculate the end of the first billing cycle after coupon was applied
  const unit = billingCycle === 'monthly' ? 'month' : 'year';
  let length = 1;
  if (billingCycle === '2year') length = 2;
  if (billingCycle === '3year') length = 3;
  const firstCycleEnd = couponAppliedAtDate.add(length, unit);

  // Coupon is valid if we haven't passed the end of the first cycle
  return now.isBefore(firstCycleEnd);
}

export function getPlanBreakdown(
  plan: LicensePlan,
  seatCount: number,
  billingCycle: BillingCycle,
  currency: 'USD' | 'AUD' | 'NZD',
  couponPercentOff: number | null,
  couponValid: boolean,
): PlanBreakdown {
  const planDetails = PLAN_DETAILS[plan];

  const baseMultiplier =
    billingCycle === 'monthly'
      ? 1
      : billingCycle === '1year'
        ? 1
        : billingCycle === '2year'
          ? 2
          : 3;
  const multiYearMultiplier = 1 - MULTI_YEAR_DISCOUNTS[billingCycle];

  const basePrice =
    billingCycle === 'monthly' ? 20 : planDetails.price * baseMultiplier;

  const additionalSeats = Math.max(seatCount - planDetails.seatCount, 0);
  const additionalSeatPrice =
    billingCycle === 'monthly'
      ? 6.66
      : planDetails.additionalSeatPrice * baseMultiplier;

  const subtotal = Math.max(
    basePrice,
    basePrice + additionalSeats * additionalSeatPrice,
  );

  const multiYearDiscount = MULTI_YEAR_DISCOUNTS[billingCycle] * 100;
  const multiYearDiscountAmount =
    billingCycle === 'monthly' || billingCycle === '1year'
      ? 'N/A'
      : `-${formatPrice(
          subtotal * MULTI_YEAR_DISCOUNTS[billingCycle],
          currency,
        )}`;

  let total = subtotal * multiYearMultiplier;

  let couponMultiplier: number | null = null;
  let coupon: { discount: number; discountAmount: string } | null = null;
  if (couponPercentOff && couponValid) {
    couponMultiplier = 1 - couponPercentOff / 100;
    coupon = {
      discount: couponPercentOff,
      discountAmount: `-${formatPrice(
        (total * couponPercentOff) / 100,
        currency,
      )}`,
    };
  }

  if (couponMultiplier !== null) total = total * couponMultiplier;

  let pricePerSeat = total / (planDetails.seatCount + additionalSeats);
  if (baseMultiplier > 1) {
    pricePerSeat = pricePerSeat / baseMultiplier;
  }

  return {
    basePrice,
    additionalSeats,
    additionalSeatPrice,
    subtotal,
    total,
    multiYearDiscount,
    multiYearDiscountAmount,
    coupon,
    pricePerSeat,
  };
}

export function isLimitedPlan(plan: LicensePlan | undefined) {
  if (!plan) return false;
  return !['trial', 'free', 'partner'].includes(plan);
}

export function getPlanIcon(plan: LicensePlan) {
  return `/icons/plan-${plan}.png`;
}

export function getSeatLimitStatus(
  license?: OrganisationLicense,
  activeStudents = 0,
): 'ok' | 'at-limit' | 'over-limit' | 'grace-ended' {
  if (!license || !isLimitedPlan(license.plan)) return 'ok';

  const now = dayjs();
  const { seatCount, seatLimitExceededAt } = license;

  if (activeStudents > seatCount) {
    if (seatLimitExceededAt) {
      const graceEnd = getSeatLimitGracePeriodEnd(seatLimitExceededAt);
      if (graceEnd && now.isAfter(dayjs(graceEnd))) {
        return 'grace-ended';
      }
    }
    return 'over-limit';
  }

  if (activeStudents === seatCount) {
    return 'at-limit';
  }

  return 'ok';
}

const SEAT_LIMIT_GRACE_PERIOD_DAYS = 7;
export function getSeatLimitGracePeriodEnd(seatLimitExceededAt: number | null) {
  if (!seatLimitExceededAt) return null;
  return dayjs(seatLimitExceededAt)
    .add(SEAT_LIMIT_GRACE_PERIOD_DAYS, 'day')
    .valueOf();
}

export function getPaymentPastDueStatus(
  license?: OrganisationLicense,
): 'ok' | 'past-due' | 'grace-ended' {
  if (!license) return 'ok';

  const { status, becamePastDueAt } = license;
  if (status !== 'past_due' || !becamePastDueAt) return 'ok';

  const now = dayjs();
  const graceEnd = getPaymentPastDueGracePeriodEnd(becamePastDueAt);
  if (graceEnd && now.isAfter(dayjs(graceEnd))) {
    return 'grace-ended';
  }

  return 'past-due';
}

const PAYMENT_PAST_DUE_GRACE_PERIOD_DAYS = 7;
export function getPaymentPastDueGracePeriodEnd(
  becamePastDueAt: number | null,
) {
  if (!becamePastDueAt) return null;
  return dayjs(becamePastDueAt)
    .add(PAYMENT_PAST_DUE_GRACE_PERIOD_DAYS, 'day')
    .valueOf();
}

export async function getCurrencyFromLocation(): Promise<
  'USD' | 'AUD' | 'NZD'
> {
  // AU => AUD, NZ => NZD, anywhere else => USD
  let country = 'US';
  try {
    const res = await fetch('https://cloudflare.com/cdn-cgi/trace');
    // Response will have a line like 'loc=NZ' (2-char ISO country code)
    const text = await res.text();
    const lines = text.split('\n');
    for (const line of lines) {
      const [key, value] = line.split('=');
      if (key === 'loc') {
        country = value;
        break;
      }
    }
  } catch (error) {
    console.error('Error getting country code', error);
  }
  if (country === 'NZ') return 'NZD';
  if (country === 'AU') return 'AUD';
  return 'USD';
}
